import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import {
  CreateFactoryContactActivityInput,
  DeleteFactoryContactActivityResponse,
  FactoryContactActivityListResponse,
  FactoryContactActivityResponse,
  FilterFactoryContactActivityInput,
  UpdateFactoryContactActivityInput,
} from 'services/interface/factoryContactActivity.interface'
import {
  createFactoryContactActivity,
  deleteFactoryContactActivity,
  getFactoryContactActivity,
  loadFactoryContactActivities,
  updateFactoryContactActivity,
} from 'services/factoryContactActivityApi'

const CONTACT_ACTIVITY_LIST_KEY = 'factoryContactActivityList'

export const useLoadContactActivities = (
  filter: FilterFactoryContactActivityInput,
  enabled = true
): UseQueryResult<FactoryContactActivityListResponse, Error> =>
  useQuery({
    queryKey: [CONTACT_ACTIVITY_LIST_KEY, filter],
    queryFn: () => loadFactoryContactActivities(filter),
    enabled: enabled && !!filter.ref_id,
  })

export const useGetContactActivity = (
  id: string | null,
  enabled = true
): UseQueryResult<FactoryContactActivityResponse, Error> =>
  useQuery({
    queryKey: ['factoryContactActivity', id],
    queryFn: () => getFactoryContactActivity(id!),
    enabled: enabled && !!id,
  })

export const useCreateContactActivity = (): UseMutationResult<
  FactoryContactActivityResponse,
  Error,
  CreateFactoryContactActivityInput,
  unknown
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input) => createFactoryContactActivity(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONTACT_ACTIVITY_LIST_KEY] })
    },
  })
}

export const useUpdateContactActivity = (): UseMutationResult<
  FactoryContactActivityResponse,
  Error,
  UpdateFactoryContactActivityInput,
  unknown
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input) => updateFactoryContactActivity(input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [CONTACT_ACTIVITY_LIST_KEY] })
      queryClient.invalidateQueries({
        queryKey: ['factoryContactActivity', variables.id],
      })
    },
  })
}

export const useDeleteContactActivity = (): UseMutationResult<
  DeleteFactoryContactActivityResponse,
  Error,
  string,
  unknown
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id) => deleteFactoryContactActivity(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONTACT_ACTIVITY_LIST_KEY] })
    },
  })
}
